import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import ListingCard from '../components/ListingCard';
import { categories } from '../mock';
import { 
  Search, 
  Filter, 
  X, 
  Loader2, 
  SlidersHorizontal,
  Grid,
  List,
  ChevronLeft,
  ChevronRight,
  SearchX,
  Heart,
  Bell,
  BellOff,
  Bookmark,
  Check
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useCountry } from '../context/CountryContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { country, countryConfig, formatCurrency } = useCountry();
  
  // Get initial values from URL
  const initialQuery = searchParams.get('q') || '';
  const initialCategory = searchParams.get('category') || '';
  const initialLocation = searchParams.get('location') || '';
  const initialCondition = searchParams.get('condition') || '';
  const initialMinPrice = searchParams.get('min_price') || '';
  const initialMaxPrice = searchParams.get('max_price') || '';
  const initialSort = searchParams.get('sort') || 'recent';
  const initialPage = parseInt(searchParams.get('page') || '1');

  // State
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  
  // Save Search State
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [notifyNewMatches, setNotifyNewMatches] = useState(true);
  const [savedSearchSettings, setSavedSearchSettings] = useState(null);
  
  // User state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Filters state
  const [filters, setFilters] = useState({
    category: initialCategory,
    location: initialLocation,
    condition: initialCondition,
    minPrice: initialMinPrice,
    maxPrice: initialMaxPrice,
    sortBy: initialSort
  });

  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    
    // Fetch saved search settings
    fetchSavedSearchSettings();
  }, []);

  useEffect(() => {
    fetchResults();
  }, [searchParams, country]);

  const fetchSavedSearchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/saved-searches/settings`);
      setSavedSearchSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch saved search settings:', err);
    }
  };

  const fetchResults = async () => {
    setLoading(true);
    try {
      const query = searchParams.get('q') || '';
      const category = searchParams.get('category') || '';
      const location = searchParams.get('location') || '';
      const condition = searchParams.get('condition') || '';
      const minPrice = searchParams.get('min_price') || '';
      const maxPrice = searchParams.get('max_price') || '';
      const sortBy = searchParams.get('sort') || 'recent';
      const page = searchParams.get('page') || '1';
      // "Near me" parameters
      const lat = searchParams.get('lat') || '';
      const lng = searchParams.get('lng') || '';
      const radiusKm = searchParams.get('radius_km') || '';

      // Build API URL with parameters
      const params = new URLSearchParams();
      if (query) params.append('search', query);
      if (category) params.append('category', category);
      if (location) params.append('location', location);
      if (condition) params.append('condition', condition);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      // Pass geo-radius search to backend
      if (lat && lng) {
        params.append('user_lat', lat);
        params.append('user_lon', lng);
        if (radiusKm) params.append('radius_km', radiusKm);
      }
      // Country isolation - only show ads from current country
      if (country) params.append('country_code', country);
      params.append('sort_by', sortBy);
      params.append('page', page);
      params.append('limit', ITEMS_PER_PAGE.toString());

      const response = await fetch(`${API_URL}/api/listings/?${params.toString()}`);
      const data = await response.json();

      if (data.listings) {
        setListings(data.listings);
        setTotalResults(data.total || 0);
        setTotalPages(Math.ceil((data.total || 0) / ITEMS_PER_PAGE));
        setCurrentPage(parseInt(page));
      }
    } catch (err) {
      console.error('Error fetching search results:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateSearchParams({ q: searchQuery, page: '1' });
  };

  const applyFilters = () => {
    const newParams = {
      q: searchQuery,
      page: '1'
    };
    
    if (filters.category) newParams.category = filters.category;
    if (filters.location) newParams.location = filters.location;
    if (filters.condition) newParams.condition = filters.condition;
    if (filters.minPrice) newParams.min_price = filters.minPrice;
    if (filters.maxPrice) newParams.max_price = filters.maxPrice;
    if (filters.sortBy) newParams.sort = filters.sortBy;

    updateSearchParams(newParams);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      location: '',
      condition: '',
      minPrice: '',
      maxPrice: '',
      sortBy: 'recent'
    });
    updateSearchParams({ q: searchQuery, page: '1' });
  };

  const updateSearchParams = (newParams) => {
    const params = new URLSearchParams();
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = filters.category || filters.location || filters.condition || filters.minPrice || filters.maxPrice;

  // Get active filter tags for display
  const getFilterTags = () => {
    const tags = [];
    if (filters.location) tags.push({ key: 'location', label: `Location: ${filters.location}` });
    if (filters.category) tags.push({ key: 'category', label: `Category: ${filters.category}` });
    if (filters.condition) tags.push({ key: 'condition', label: `Condition: ${filters.condition}` });
    if (filters.minPrice || filters.maxPrice) {
      const priceLabel = filters.minPrice && filters.maxPrice 
        ? `${formatCurrency(filters.minPrice)} - ${formatCurrency(filters.maxPrice)}`
        : filters.minPrice ? `Min Rs ${filters.minPrice}` : `Max Rs ${filters.maxPrice}`;
      tags.push({ key: 'price', label: priceLabel });
    }
    return tags;
  };

  const removeFilterTag = (key) => {
    const newFilters = { ...filters };
    if (key === 'price') {
      newFilters.minPrice = '';
      newFilters.maxPrice = '';
    } else {
      newFilters[key] = '';
    }
    setFilters(newFilters);
    
    const params = new URLSearchParams(searchParams);
    if (key === 'price') {
      params.delete('min_price');
      params.delete('max_price');
    } else {
      params.delete(key);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  // Save Search Functions
  const generateSearchName = () => {
    const parts = [];
    if (searchQuery) parts.push(`"${searchQuery}"`);
    if (filters.category) parts.push(filters.category);
    if (filters.location) parts.push(`in ${filters.location}`);
    return parts.length > 0 ? parts.join(' ') : 'My Search';
  };

  const openSaveSearchDialog = () => {
    if (!isLoggedIn) {
      toast({
        title: "Login Required",
        description: "Please login to save searches",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    setSearchName(generateSearchName());
    setShowSaveDialog(true);
  };

  const handleSaveSearch = async () => {
    if (!searchName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a name for this search",
        variant: "destructive"
      });
      return;
    }

    setSavingSearch(true);
    try {
      const token = localStorage.getItem('token');
      const criteria = {
        search: searchQuery || undefined,
        category: filters.category || undefined,
        location: filters.location || undefined,
        condition: filters.condition || undefined,
        min_price: filters.minPrice || undefined,
        max_price: filters.maxPrice || undefined
      };
      
      // Remove undefined values
      Object.keys(criteria).forEach(key => criteria[key] === undefined && delete criteria[key]);

      await axios.post(
        `${API_URL}/api/saved-searches/`,
        {
          name: searchName,
          criteria,
          notify: notifyNewMatches
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Search Saved!",
        description: notifyNewMatches 
          ? "You'll be notified when new listings match this search"
          : "Search saved to your profile",
      });
      setShowSaveDialog(false);
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to save search",
        variant: "destructive"
      });
    } finally {
      setSavingSearch(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Search Header - Title Only (Search is in Navbar) */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">
                {initialQuery ? `Search results for "${initialQuery}"` : 'Browse All Listings'}
              </h1>
              {initialLocation && (
                <p className="text-blue-100 mt-1">in {initialLocation}</p>
              )}
            </div>
            {/* Save Search Button */}
            {savedSearchSettings?.enabled && (
              <Button
                onClick={openSaveSearchDialog}
                variant="secondary"
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <Bookmark className="h-4 w-4 mr-2" />
                Save Search
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Results Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <p className="text-gray-600">
              {loading ? 'Searching...' : `${totalResults} results found`}
            </p>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-blue-600">
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Sort Dropdown */}
            <Select
              value={filters.sortBy}
              onValueChange={(value) => {
                setFilters({ ...filters, sortBy: value });
                const params = new URLSearchParams(searchParams);
                params.set('sort', value);
                params.set('page', '1');
                setSearchParams(params);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most Recent</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="popular">Most Popular</SelectItem>
              </SelectContent>
            </Select>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-blue-50 border-blue-500' : ''}
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge className="ml-2 bg-blue-600">Active</Badge>
              )}
            </Button>

            {/* View Mode Toggle */}
            <div className="hidden sm:flex border rounded-lg overflow-hidden">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-none"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Active Filter Tags */}
        {getFilterTags().length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {getFilterTags().map((tag) => (
              <Badge 
                key={tag.key} 
                variant="secondary" 
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer flex items-center gap-1"
                onClick={() => removeFilterTag(tag.key)}
              >
                {tag.label}
                <X className="h-3 w-3" />
              </Badge>
            ))}
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Category */}
                <div>
                  <Label className="mb-2 block">Category</Label>
                  <Select
                    value={filters.category || "all"}
                    onValueChange={(value) => setFilters({ ...filters, category: value === "all" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.slug}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div>
                  <Label className="mb-2 block">Location</Label>
                  <Input
                    placeholder="Enter city or area"
                    value={filters.location}
                    onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  />
                </div>

                {/* Condition */}
                <div>
                  <Label className="mb-2 block">Condition</Label>
                  <Select
                    value={filters.condition || "any"}
                    onValueChange={(value) => setFilters({ ...filters, condition: value === "any" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any Condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any Condition</SelectItem>
                      <SelectItem value="Brand New">Brand New</SelectItem>
                      <SelectItem value="Like New">Like New</SelectItem>
                      <SelectItem value="Used">Used</SelectItem>
                      <SelectItem value="For Parts">For Parts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Range */}
                <div>
                  <Label className="mb-2 block">Price Range (Rs)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={filters.minPrice}
                      onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                      className="w-1/2"
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                      className="w-1/2"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={clearFilters}>
                  Reset
                </Button>
                <Button onClick={applyFilters} className="bg-gradient-to-r from-blue-600 to-purple-600">
                  Apply Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          </div>
        ) : listings.length > 0 ? (
          <>
            {/* Grid View */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {listings.map((listing) => (
                  <ListingCard key={listing.id} listing={listing} />
                ))}
              </div>
            ) : (
              /* List View */
              <div className="space-y-4">
                {listings.map((listing) => (
                  <Card 
                    key={listing.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/listing/${listing.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={listing.images?.[0]?.startsWith('http') 
                              ? listing.images[0] 
                              : listing.images?.[0] 
                                ? `${API_URL}${listing.images[0]}`
                                : 'https://via.placeholder.com/128?text=No+Image'
                            }
                            alt={listing.title}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/128?text=No+Image'; }}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-lg mb-1">{listing.title}</h3>
                              <p className="text-2xl font-bold text-blue-600">
                                {formatCurrency ? formatCurrency(listing.price) : `${countryConfig?.currency_symbol || 'Rs.'} ${listing.price?.toLocaleString()}`}
                              </p>
                            </div>
                            {listing.featured && (
                              <Badge className="bg-yellow-500">Featured</Badge>
                            )}
                          </div>
                          <p className="text-gray-500 text-sm mt-2 line-clamp-2">
                            {listing.description}
                          </p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                            <span>{listing.location}</span>
                            <span>{listing.condition}</span>
                            <span>{listing.views || 0} views</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="w-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        ) : (
          /* No Results */
          <div className="text-center py-20">
            <SearchX className="h-20 w-20 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-600 mb-2">No results found</h2>
            <p className="text-gray-500 mb-6">
              {initialQuery 
                ? `We couldn't find any listings matching "${initialQuery}"`
                : 'No listings available with the selected filters'
              }
            </p>
            <div className="flex justify-center gap-4">
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
              <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-blue-600 to-purple-600">
                Browse All Listings
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Save Search Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="h-5 w-5 text-blue-600" />
              Save This Search
            </DialogTitle>
            <DialogDescription>
              Save your search criteria for quick access later
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Search Name</Label>
              <Input
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="e.g., iPhone in Colombo"
              />
            </div>

            {/* Search Criteria Summary */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Search Criteria:</p>
              <div className="flex flex-wrap gap-2">
                {searchQuery && (
                  <Badge variant="secondary">Query: {searchQuery}</Badge>
                )}
                {filters.category && (
                  <Badge variant="secondary">Category: {filters.category}</Badge>
                )}
                {filters.location && (
                  <Badge variant="secondary">Location: {filters.location}</Badge>
                )}
                {filters.condition && (
                  <Badge variant="secondary">Condition: {filters.condition}</Badge>
                )}
                {(filters.minPrice || filters.maxPrice) && (
                  <Badge variant="secondary">
                    Price: {countryConfig?.currency_symbol || 'Rs.'} {filters.minPrice || '0'} - {countryConfig?.currency_symbol || 'Rs.'} {filters.maxPrice || '∞'}
                  </Badge>
                )}
                {!searchQuery && !filters.category && !filters.location && !filters.condition && !filters.minPrice && !filters.maxPrice && (
                  <span className="text-sm text-gray-500">All listings</span>
                )}
              </div>
            </div>

            {/* Notification Toggle */}
            {savedSearchSettings?.notifications_enabled && (
              <div 
                onClick={() => setNotifyNewMatches(!notifyNewMatches)}
                className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  notifyNewMatches ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  {notifyNewMatches ? (
                    <Bell className="h-5 w-5 text-blue-600" />
                  ) : (
                    <BellOff className="h-5 w-5 text-gray-400" />
                  )}
                  <div>
                    <p className="font-medium">Notify me of new matches</p>
                    <p className="text-sm text-gray-500">
                      Get alerts when new listings match this search
                    </p>
                  </div>
                </div>
                {notifyNewMatches && (
                  <Check className="h-5 w-5 text-blue-600" />
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSearch} 
              disabled={savingSearch}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
            >
              {savingSearch ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Bookmark className="mr-2 h-4 w-4" />
                  Save Search
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SearchResults;
